<template>
  <FlashNotifications ref="flash" />

  <!-- ── Withdraw confirmation modal ── -->
  <Transition name="modal">
    <div v-if="showWithdrawModal"
      class="fixed inset-0 z-50 flex items-center justify-center px-4"
      @click.self="showWithdrawModal = false">
      <div class="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-xl w-full max-w-sm p-6 border border-gray-100 dark:border-gray-700">
        <div class="flex flex-col items-center text-center">
          <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
            </svg>
          </div>
          <h3 class="text-base font-bold text-gray-900 dark:text-white mb-2">Withdraw verification request?</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
            Your submitted documents will be removed. You will need to start the verification process again.
          </p>
          <div class="flex gap-3 w-full">
            <button @click="showWithdrawModal = false"
              class="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
              Cancel
            </button>
            <button @click="confirmWithdraw" :disabled="withdrawing"
              class="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {{ withdrawing ? 'Withdrawing...' : 'Yes, withdraw' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- ── Revoke verification modal ── -->
  <Transition name="modal">
    <div v-if="showRevokeModal"
      class="fixed inset-0 z-50 flex items-center justify-center px-4"
      @click.self="showRevokeModal = false">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-xl w-full max-w-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="p-6">
          <div class="flex flex-col items-center text-center">
            <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"/>
              </svg>
            </div>
            <h3 class="text-base font-bold text-gray-900 dark:text-white mb-2">Remove verification?</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-2">
              Your verified badge will be removed from your public profile immediately.
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mb-6 bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
              To get verified again, you will need to go through the full verification process. You will receive an email confirmation of this change.
            </p>
            <div class="flex gap-3 w-full">
              <button @click="showRevokeModal = false"
                class="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
                Keep it
              </button>
              <button @click="revokeVerification" :disabled="revoking"
                class="flex-1 py-2.5 text-sm font-semibold rounded-xl bg-gray-900 dark:bg-gray-100 hover:bg-gray-700 dark:hover:bg-white text-white dark:text-gray-900 transition-colors disabled:opacity-50">
                {{ revoking ? 'Removing...' : 'Yes, remove it' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <!-- ═══════════════════════ MOBILE ═══════════════════════ -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">

    <!-- Mobile skeleton -->
    <template v-if="loading">
      <div class="animate-pulse pb-20">
        <div class="px-4 pt-6 pb-2 h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
          <div class="px-4 py-5 flex flex-col items-center gap-3">
            <div class="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3 w-48 bg-gray-100 dark:bg-gray-800 rounded"></div>
            <div class="h-3 w-44 bg-gray-100 dark:bg-gray-800 rounded"></div>
            <div class="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-xl mt-1"></div>
          </div>
        </div>
        <div class="px-4 pt-5 pb-2 h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          <div v-for="i in 4" :key="i" class="px-4 py-3 flex items-center gap-3">
            <div class="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
            <div class="h-3 flex-1 bg-gray-100 dark:bg-gray-800 rounded"></div>
          </div>
        </div>
      </div>
    </template>

    <!-- Mobile error -->
    <template v-else-if="loadError">
      <div class="flex flex-col items-center justify-center py-24 px-6 text-center">
        <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ loadError }}</p>
        <button @click="loadVerification" class="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">Try again</button>
      </div>
    </template>

    <template v-else>
      <div class="pb-28">

        <!-- ── Unverified start ── -->
        <template v-if="!verification && !showForm">
          <div class="px-4 pt-8 pb-2 flex flex-col items-center text-center">
            <div class="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z"/>
                <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
            </div>
            <h2 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none mb-2">Get Verified</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-xs">
              A verified badge on your profile shows visitors you are a confirmed, real person.
            </p>
          </div>

          <div class="px-4 pt-5">
            <button @click="showForm = true"
              class="w-full py-3.5 bg-blue-600 active:bg-blue-700 text-white text-[15px] font-semibold rounded-2xl transition-colors">
              Start Verification
            </button>
          </div>

          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">What you'll need</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <div v-for="doc in documentTypes" :key="doc.value" class="flex items-center gap-3 px-4 py-3.5">
              <span class="text-xl flex-shrink-0">{{ doc.icon }}</span>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ doc.label }}</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Front and back photo required</p>
              </div>
            </div>
          </div>
          <p class="px-4 pt-3 pb-1 text-xs text-gray-400 dark:text-gray-500 text-center">Upload at least 1 document to start</p>
        </template>

        <!-- ── Upload form ── -->
        <template v-else-if="showForm">

          <!-- Sticky mini-header with submit -->
          <div class="sticky top-[6.75rem] z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700/50">
            <div class="flex items-center justify-between px-4 h-12">
              <button @click="cancelForm" class="text-sm font-medium text-blue-600 dark:text-blue-400">Cancel</button>
              <p class="text-sm font-semibold text-gray-900 dark:text-white">Documents</p>
              <button @click="submitVerification" :disabled="!canSubmit || submitting"
                class="text-sm font-semibold transition-colors"
                :class="canSubmit && !submitting ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600'">
                {{ submitting ? 'Sending...' : 'Submit' }}
              </button>
            </div>
          </div>

          <!-- Name-match notice -->
          <div class="mx-4 mt-4 flex items-start gap-2.5 bg-amber-50 dark:bg-amber-900/15 rounded-xl px-3.5 py-3">
            <svg class="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
            </svg>
            <p class="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
              Your <strong>full name</strong> and <strong>profile picture</strong> must match the identity document you upload.
            </p>
          </div>

          <!-- One card per document, full-width list rows -->
          <div v-for="(doc, idx) in documentEntries" :key="doc.id" class="mt-5">
            <div class="flex items-center justify-between px-4 pb-2">
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Document {{ idx + 1 }}</p>
              <button v-if="documentEntries.length > 1" @click="removeDocument(idx)"
                class="text-xs font-semibold text-red-500">Remove</button>
            </div>
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">

              <!-- Type picker row — native select layered over styled row -->
              <div class="relative flex items-center justify-between px-4 py-3.5">
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Document Type</p>
                <div class="flex items-center gap-1.5 pointer-events-none">
                  <span class="text-sm" :class="doc.type ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'">
                    {{ doc.type ? (documentTypes.find(d => d.value === doc.type)?.icon + ' ' + documentTypes.find(d => d.value === doc.type)?.label) : 'Select...' }}
                  </span>
                  <svg class="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
                <select v-model="doc.type" class="absolute inset-0 opacity-0 cursor-pointer w-full h-full">
                  <option value="" disabled>Select document type</option>
                  <option v-for="dt in availableTypesFor(idx)" :key="dt.value" :value="dt.value">{{ dt.icon }} {{ dt.label }}</option>
                </select>
              </div>

              <!-- Front side upload row -->
              <label class="flex items-center justify-between px-4 py-3.5 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
                <input type="file" accept="image/*,application/pdf" class="hidden" @change="(e) => handleFileChange(e, idx, 'front')"/>
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    :class="doc.frontPreview ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-100 dark:bg-gray-800'">
                    <svg v-if="!doc.frontPreview" class="w-4.5 h-4.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"/>
                    </svg>
                    <svg v-else class="w-4.5 h-4.5 text-blue-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Front Side</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ doc.frontFile ? doc.frontFile.name : 'Tap to upload photo' }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <img v-if="doc.frontPreview" :src="doc.frontPreview" class="w-10 h-10 rounded-lg object-cover"/>
                  <svg class="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              </label>

              <!-- Back side upload row -->
              <label class="flex items-center justify-between px-4 py-3.5 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
                <input type="file" accept="image/*,application/pdf" class="hidden" @change="(e) => handleFileChange(e, idx, 'back')"/>
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    :class="doc.backPreview ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-100 dark:bg-gray-800'">
                    <svg v-if="!doc.backPreview" class="w-4.5 h-4.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"/>
                    </svg>
                    <svg v-else class="w-4.5 h-4.5 text-blue-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Back Side</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ doc.backFile ? doc.backFile.name : 'Tap to upload photo' }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <img v-if="doc.backPreview" :src="doc.backPreview" class="w-10 h-10 rounded-lg object-cover"/>
                  <svg class="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
                  </svg>
                </div>
              </label>

            </div>
          </div>

          <!-- Add document row -->
          <div v-if="hasAvailableTypes" class="mt-3">
            <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
              <button @click="addDocument" class="w-full flex items-center gap-3 px-4 py-3.5 active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
                <div class="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                  </svg>
                </div>
                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Add Another Document</p>
              </button>
            </div>
          </div>

          <!-- Privacy consent -->
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Privacy</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
            <label class="flex items-center justify-between px-4 py-3.5 cursor-pointer">
              <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed flex-1 pr-4">
                Documents stored securely, used only to verify my identity
              </p>
              <div class="flex-shrink-0">
                <input type="checkbox" v-model="privacyAccepted" class="sr-only"/>
                <div class="w-11 h-6 rounded-full transition-colors relative flex-shrink-0"
                  :class="privacyAccepted ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'">
                  <div class="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200"
                    :class="privacyAccepted ? 'translate-x-5' : 'translate-x-0.5'"></div>
                </div>
              </div>
            </label>
          </div>
        </template>

        <!-- ── pending_review ── -->
        <template v-else-if="verification?.status === 'pending_review'">
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</p>
          <MobileTimeline :step="1" />

          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Details</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="px-4 py-3.5">
              <p class="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Status</p>
              <p class="text-sm font-medium text-amber-600 dark:text-amber-400">Under review</p>
            </div>
            <div class="px-4 py-3.5">
              <p class="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Submitted</p>
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ formatDate(verification.submitted_at) }}</p>
            </div>
            <div class="px-4 py-3.5">
              <p class="text-xs text-gray-400 dark:text-gray-500 mb-1">Next step</p>
              <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">We will email <strong class="text-gray-900 dark:text-white">{{ userEmail }}</strong> to schedule a short video call.</p>
            </div>
          </div>

          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Actions</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
            <button @click="showWithdrawModal = true"
              class="w-full px-4 py-3.5 text-sm font-medium text-red-500 text-left active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
              Withdraw verification request
            </button>
          </div>
        </template>

        <!-- ── call_scheduled ── -->
        <template v-else-if="verification?.status === 'call_scheduled'">
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Status</p>
          <MobileTimeline :step="2" />

          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Your call</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <a v-if="verification.call_link" :href="verification.call_link" target="_blank"
              class="flex items-center justify-between px-4 py-3.5 active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
              <span class="text-sm font-medium text-blue-600 dark:text-blue-400">Book your slot</span>
              <svg class="w-4 h-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
              </svg>
            </a>
          </div>
          <p class="px-4 pt-3 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
            Already booked a time? You're all set. We'll be in touch once your call is confirmed.
          </p>

          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Actions</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
            <button @click="showWithdrawModal = true"
              class="w-full px-4 py-3.5 text-sm font-medium text-red-500 text-left active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
              Withdraw verification request
            </button>
          </div>
        </template>

        <!-- ── approved ── -->
        <template v-else-if="verification?.status === 'approved'">
          <div class="mx-4 mt-6 rounded-3xl overflow-hidden" style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 60%, #e0e7ff 100%)">
            <div class="dark:hidden px-6 pt-10 pb-8 flex flex-col items-center text-center">
              <div class="w-20 h-20 rounded-full bg-white/70 shadow-sm flex items-center justify-center mb-5 ring-4 ring-white/50">
                <svg class="w-10 h-10" viewBox="0 0 24 24" fill="none">
                  <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                  <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h2 class="text-2xl font-bold text-gray-900 tracking-tight mb-2">Account Verified</h2>
              <p class="text-sm text-blue-900/60 leading-relaxed mb-1">Your verified badge is live on your public profile.</p>
              <p v-if="verification.reviewed_at" class="text-xs text-blue-900/40 mb-6">Since {{ formatDate(verification.reviewed_at) }}</p>
              <a v-if="userUrl" :href="'/' + userUrl" target="_blank"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-2xl transition-colors mb-3">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                </svg>
                View profile
              </a>
              <button @click="showRevokeModal = true" class="text-xs text-blue-900/30 hover:text-blue-900/50 transition-colors">Remove verification</button>
            </div>
            <!-- Dark mode fallback (white card) -->
            <div class="hidden dark:flex flex-col items-center text-center px-6 pt-10 pb-8 bg-[#1C2128]">
              <div class="w-14 h-14 rounded-full bg-blue-900/30 flex items-center justify-center mb-4">
                <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                  <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h2 class="text-xl font-bold text-white mb-1">Account Verified</h2>
              <p class="text-sm text-gray-400 mb-1">Your verified badge is live on your public profile.</p>
              <p v-if="verification.reviewed_at" class="text-xs text-gray-500 mb-5">Since {{ formatDate(verification.reviewed_at) }}</p>
              <a v-if="userUrl" :href="'/' + userUrl" target="_blank" class="w-full py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl text-center mb-3">View Profile</a>
              <button @click="showRevokeModal = true" class="text-xs text-gray-600 hover:text-gray-400 transition-colors">Remove verification</button>
            </div>
          </div>
        </template>

        <!-- ── rejected ── -->
        <template v-else-if="verification?.status === 'rejected'">
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Verification</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
            <div class="px-4 py-3.5">
              <p class="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Status</p>
              <p class="text-sm font-medium text-red-500">Unsuccessful</p>
            </div>
            <div v-if="verification.rejection_reason" class="px-4 py-3.5">
              <p class="text-xs text-gray-400 dark:text-gray-500 mb-1">Reason</p>
              <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{{ verification.rejection_reason }}</p>
            </div>
          </div>

          <div class="px-4 pt-5">
            <button @click="showForm = true"
              class="w-full py-3.5 bg-blue-600 active:bg-blue-700 text-white text-[15px] font-semibold rounded-2xl transition-colors">
              Resubmit Application
            </button>
          </div>

          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Actions</p>
          <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden">
            <button @click="showWithdrawModal = true"
              class="w-full px-4 py-3.5 text-sm font-medium text-red-500 text-left active:bg-gray-50 dark:active:bg-gray-800/50 transition-colors">
              Withdraw verification request
            </button>
          </div>
        </template>

      </div>
    </template>
  </div>

  <!-- ═══════════════════════ DESKTOP ═══════════════════════ -->
  <div class="hidden sm:block min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Desktop skeleton -->
    <template v-if="loading">
      <div class="animate-pulse">
        <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
          <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
            <div class="flex items-center gap-2.5">
              <div class="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
              <div class="space-y-1.5">
                <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="max-w-5xl mx-auto px-6 py-6">
          <div class="flex flex-col lg:flex-row gap-5">
            <div class="flex-1 min-w-0 space-y-4">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800"></div>
                  <div class="space-y-1.5">
                    <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div class="h-2.5 w-44 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
                <div class="px-6 py-8 flex flex-col items-center gap-3">
                  <div class="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                  <div class="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-3 w-64 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div class="h-3 w-56 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  <div class="h-10 w-44 bg-gray-200 dark:bg-gray-700 rounded-lg mt-2"></div>
                </div>
              </div>
            </div>
            <div class="w-full lg:w-64 flex-shrink-0 order-first lg:order-last">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  <div class="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div class="divide-y divide-gray-100 dark:divide-gray-700">
                  <div v-for="i in 4" :key="i" class="flex items-center gap-2.5 px-4 py-3">
                    <div class="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
                    <div class="h-2.5 flex-1 bg-gray-100 dark:bg-gray-800 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Desktop error -->
    <template v-else-if="loadError">
      <div class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ loadError }}</p>
        <button @click="loadVerification" class="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">Try again</button>
      </div>
    </template>

    <template v-else>
      <!-- Sub-page header -->
      <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 sticky top-14 z-10">
        <div class="max-w-5xl mx-auto px-6 flex items-center justify-between h-14">
          <div class="flex items-center gap-2.5">
            <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z"/>
                <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
            </div>
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Get Verified</h1>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Identity verification for your public profile</p>
            </div>
          </div>
          <div class="h-7 flex items-center">
            <Transition name="fade">
              <span v-if="submitting" key="submitting"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-xs font-medium text-blue-600 dark:text-blue-400">
                <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Submitting
              </span>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Page content -->
      <div class="max-w-5xl mx-auto px-6 py-6">
        <div class="flex flex-col lg:flex-row gap-5">

          <!-- Left column -->
          <div :class="verification?.status === 'approved' ? 'w-full' : 'flex-1 min-w-0'" class="space-y-4">

            <!-- ── Unverified start ── -->
            <template v-if="!verification && !showForm">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"/>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Identity Verification</h2>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Earn a verified badge on your public profile</p>
                  </div>
                </div>
                <div class="px-6 py-8 flex flex-col items-center text-center">
                  <div class="w-14 h-14 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
                    <svg class="w-7 h-7 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z"/>
                      <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                    </svg>
                  </div>
                  <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-2">Get your profile verified</h2>
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm mb-6">
                    A verified badge shows visitors you are a real, confirmed person. It builds trust and increases booking confidence.
                  </p>
                  <ul class="text-left space-y-2 mb-7 w-full max-w-sm">
                    <li v-for="benefit in benefits" :key="benefit" class="flex items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                      <svg class="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                      </svg>
                      {{ benefit }}
                    </li>
                  </ul>
                  <button @click="showForm = true"
                    class="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                    Start Verification
                  </button>
                </div>
              </div>
            </template>

            <!-- ── Upload form ── -->
            <template v-else-if="showForm">
              <div class="flex items-center justify-between mb-1">
                <button @click="cancelForm"
                  class="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
                  </svg>
                  Cancel
                </button>
                <button @click="submitVerification" :disabled="!canSubmit || submitting"
                  class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors"
                  :class="canSubmit && !submitting
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'">
                  <svg v-if="submitting" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  {{ submitting ? 'Submitting...' : 'Submit for Review' }}
                </button>
              </div>

              <!-- Name-match notice -->
              <div class="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-100 dark:border-amber-800/30 rounded-xl px-5 py-3.5">
                <svg class="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
                </svg>
                <p class="text-sm text-amber-700 dark:text-amber-300 leading-relaxed">
                  Your <strong>full name</strong> and <strong>profile picture</strong> must match the identity document you upload.
                </p>
              </div>

              <!-- Single card: all documents + privacy in one compact view -->
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <!-- Card header -->
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <svg class="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Identity Documents</h3>
                      <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
                        {{ completeCount >= 1 ? completeCount + ' of ' + documentEntries.length + ' complete' : 'Front and back required for each document' }}
                      </p>
                    </div>
                  </div>
                  <button v-if="hasAvailableTypes" @click="addDocument"
                    class="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                    </svg>
                    Add document
                  </button>
                </div>
                <!-- Document grid -->
                <div class="px-6 py-5">
                  <div class="grid grid-cols-2 gap-3">
                    <div v-for="(doc, idx) in documentEntries" :key="doc.id"
                      class="bg-gray-50 dark:bg-[#161b22] rounded-xl p-3.5 space-y-3">
                      <!-- Slot header -->
                      <div class="flex items-center justify-between">
                        <span class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Document {{ idx + 1 }}</span>
                        <button v-if="documentEntries.length > 1" @click="removeDocument(idx)"
                          class="text-[10px] text-red-400 hover:text-red-500 font-semibold transition-colors">Remove</button>
                      </div>
                      <!-- Type dropdown -->
                      <select v-model="doc.type"
                        class="w-full text-sm px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1C2128] text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all">
                        <option value="" disabled>Select document type...</option>
                        <option v-for="dt in availableTypesFor(idx)" :key="dt.value" :value="dt.value">{{ dt.icon }} {{ dt.label }}</option>
                      </select>
                      <!-- Front + Back upload squares -->
                      <div class="grid grid-cols-2 gap-2">
                        <div v-for="side in ['front', 'back']" :key="side">
                          <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">{{ side === 'front' ? 'Front' : 'Back' }}</p>
                          <label class="cursor-pointer block group">
                            <input type="file" accept="image/*,application/pdf" class="hidden" @change="(e) => handleFileChange(e, idx, side)"/>
                            <div class="h-[72px] rounded-lg border-2 border-dashed overflow-hidden flex flex-col items-center justify-center transition-all"
                              :class="doc[side + 'Preview']
                                ? 'border-blue-400 dark:border-blue-500'
                                : 'border-gray-200 dark:border-gray-700 group-hover:border-blue-400 dark:group-hover:border-blue-500 group-hover:bg-blue-50/30 dark:group-hover:bg-blue-900/5'">
                              <img v-if="doc[side + 'Preview']" :src="doc[side + 'Preview']" class="w-full h-full object-cover"/>
                              <template v-else>
                                <svg class="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"/>
                                </svg>
                                <span class="text-[10px] text-gray-400 dark:text-gray-600 group-hover:text-blue-500 transition-colors mt-1">Click to upload</span>
                              </template>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Privacy footer -->
                  <div class="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-start gap-3">
                    <div class="mt-0.5 flex-shrink-0">
                      <input type="checkbox" v-model="privacyAccepted" class="sr-only"/>
                      <div class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors cursor-pointer"
                        :class="privacyAccepted ? 'border-blue-600 bg-blue-600' : 'border-gray-300 dark:border-gray-600'"
                        @click="privacyAccepted = !privacyAccepted">
                        <svg v-if="privacyAccepted" class="w-3 h-3 text-white" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                        </svg>
                      </div>
                    </div>
                    <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed flex-1">
                      My documents will be stored securely and used only to verify my identity. They will not be shared with third parties.
                    </p>
                  </div>
                </div>
              </div>
            </template>

            <!-- ── pending_review ── -->
            <template v-else-if="verification?.status === 'pending_review'">
              <DesktopTimeline :step="1" />
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Documents Under Review</h2>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Submitted {{ formatDate(verification.submitted_at) }}</p>
                  </div>
                </div>
                <div class="px-6 py-5">
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-1">
                    Our team is reviewing your documents. We will email
                    <strong class="text-gray-700 dark:text-gray-300">{{ userEmail }}</strong>
                    to schedule a brief video call to complete your verification.
                  </p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-3">The process typically takes 2 to 3 business days.</p>
                </div>
              </div>
              <div class="flex justify-end">
                <button @click="showWithdrawModal = true"
                  class="text-sm text-red-500 hover:text-red-600 font-medium transition-colors">
                  Withdraw verification request
                </button>
              </div>
            </template>

            <!-- ── call_scheduled ── -->
            <template v-else-if="verification?.status === 'call_scheduled'">
              <DesktopTimeline :step="2" />
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Book Your Verification Call</h2>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Pick a time that works for you</p>
                  </div>
                </div>
                <div class="px-6 py-5">
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-4">
                    We have reviewed your documents. Use the link below to book a short video call at a time that suits you.
                  </p>
                  <a v-if="verification.call_link" :href="verification.call_link" target="_blank"
                    class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
                    </svg>
                    Book Your Slot
                  </a>
                  <p class="mt-3 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                    Already booked a time? You're all set. We'll be in touch once your call is confirmed.
                  </p>
                </div>
              </div>
              <div class="flex justify-end mt-4">
                <button @click="showWithdrawModal = true"
                  class="text-sm text-red-500 hover:text-red-600 font-medium transition-colors">
                  Withdraw verification request
                </button>
              </div>
            </template>

            <!-- ── approved ── -->
            <template v-else-if="verification?.status === 'approved'">
              <div class="rounded-2xl overflow-hidden" style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 60%, #e0e7ff 100%)">
                <div class="dark:hidden relative px-8 py-14 flex flex-col items-center text-center">
                  <div class="w-20 h-20 rounded-full bg-white/70 shadow-md flex items-center justify-center mb-6 ring-4 ring-white/60">
                    <svg class="w-10 h-10" viewBox="0 0 24 24" fill="none">
                      <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                      <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <h2 class="text-3xl font-bold text-gray-900 tracking-tight mb-3">Account Verified</h2>
                  <p class="text-base text-blue-900/60 leading-relaxed max-w-md mb-2">
                    Your verified badge is live on your public profile. Visitors know you are a confirmed, real person.
                  </p>
                  <p v-if="verification.reviewed_at" class="text-sm text-blue-900/40 mb-8">
                    Since {{ formatDate(verification.reviewed_at) }}
                  </p>
                  <div class="flex items-center gap-5">
                    <a v-if="userUrl" :href="'/' + userUrl" target="_blank"
                      class="inline-flex items-center gap-2.5 px-7 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-2xl shadow-sm transition-colors">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                      </svg>
                      View your profile
                    </a>
                    <button @click="showRevokeModal = true" class="text-sm text-blue-900/30 hover:text-blue-900/50 transition-colors">
                      Remove verification
                    </button>
                  </div>
                </div>
                <!-- Dark mode: clean white-on-dark card -->
                <div class="hidden dark:flex flex-col items-center text-center px-8 py-14 bg-[#1C2128]">
                  <div class="w-16 h-16 rounded-full bg-blue-900/30 flex items-center justify-center mb-5">
                    <svg class="w-9 h-9" viewBox="0 0 24 24" fill="none">
                      <path d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91-1.01-1-2.52-1.26-3.9-.8C14.86 2.87 13.61 2 12.18 2c-1.43 0-2.67.88-3.34 2.19-1.39-.46-2.9-.2-3.91.81-1.01 1-1.27 2.51-.81 3.9C2.87 9.33 2 10.58 2 12c0 1.43.88 2.67 2.19 3.34-.46 1.39-.2 2.9.81 3.91 1 1.01 2.51 1.27 3.9.81.67 1.31 1.92 2.19 3.35 2.19 1.43 0 2.68-.88 3.34-2.19 1.39.46 2.9.2 3.91-.81 1.01-1 1.27-2.52.81-3.9C21.13 14.67 22.25 13.43 22.25 12z" fill="#0866FF"/>
                      <path d="M8 12.5l3 3 5-6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  <h2 class="text-2xl font-bold text-white mb-2">Account Verified</h2>
                  <p class="text-sm text-gray-400 max-w-sm mb-1 leading-relaxed">Your verified badge is live on your public profile.</p>
                  <p v-if="verification.reviewed_at" class="text-xs text-gray-500 mb-7">Since {{ formatDate(verification.reviewed_at) }}</p>
                  <div class="flex items-center gap-5">
                    <a v-if="userUrl" :href="'/' + userUrl" target="_blank"
                      class="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors">View your profile</a>
                    <button @click="showRevokeModal = true" class="text-sm text-gray-600 hover:text-gray-400 transition-colors">Remove verification</button>
                  </div>
                </div>
              </div>
            </template>

            <!-- ── rejected ── -->
            <template v-else-if="verification?.status === 'rejected'">
              <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                  <div class="w-6 h-6 rounded-md bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                    </svg>
                  </div>
                  <div>
                    <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Verification Unsuccessful</h2>
                    <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
                      Resubmit your application with updated documents
                    </p>
                  </div>
                </div>
                <div class="px-6 py-5">
                  <div v-if="verification.rejection_reason"
                    class="bg-red-50 dark:bg-red-900/15 border border-red-100 dark:border-red-800/30 rounded-lg px-4 py-3 mb-4">
                    <p class="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">Reason</p>
                    <p class="text-sm text-red-700 dark:text-red-300 leading-relaxed">{{ verification.rejection_reason }}</p>
                  </div>
                  <button @click="showForm = true"
                    class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm transition-colors">
                    Resubmit Application
                  </button>
                </div>
              </div>
              <div class="flex justify-end mt-4">
                <button @click="showWithdrawModal = true"
                  class="text-sm text-red-500 hover:text-red-600 font-medium transition-colors">
                  Withdraw verification request
                </button>
              </div>
            </template>

          </div>

          <!-- Right column — hidden when verified (full-width hero takes over) -->
          <div v-if="verification?.status !== 'approved'" class="w-full lg:w-64 flex-shrink-0 order-first lg:order-last space-y-4">

            <!-- Accepted documents card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700">
                <h3 class="text-xs font-semibold text-gray-900 dark:text-white leading-none">Accepted Documents</h3>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Front and back photo required for each</p>
              </div>
              <div class="divide-y divide-gray-100 dark:divide-gray-700/60">
                <div v-for="doc in documentTypes" :key="doc.value" class="flex items-center gap-2.5 px-4 py-2.5">
                  <span class="text-base flex-shrink-0">{{ doc.icon }}</span>
                  <span class="text-sm text-gray-700 dark:text-gray-200">{{ doc.label }}</span>
                </div>
              </div>
            </div>

            <!-- How it works card -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="px-4 py-3.5 border-b border-gray-100 dark:border-gray-700">
                <h3 class="text-xs font-semibold text-gray-900 dark:text-white leading-none">How it works</h3>
              </div>
              <div class="px-4 py-3 space-y-3">
                <div v-for="(step, i) in howItWorks" :key="i" class="flex items-start gap-2.5">
                  <div class="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span class="text-[10px] font-bold text-blue-600 dark:text-blue-400">{{ i + 1 }}</span>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ step }}</p>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'redaxios'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import { useSettingsStore } from 'controllers/vue_components/settings/v1/store'

// ── Inline sub-components ────────────────────────────────────────────────────

const TIMELINE_STEPS = ['Documents Submitted', 'Call Scheduled', 'Decision']

const MobileTimeline = {
  props: ['step'],
  template: `
    <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 px-4 py-4">
      <div class="flex items-center">
        <template v-for="(s, i) in steps" :key="i">
          <div class="flex items-center" :class="i < steps.length - 1 ? 'flex-1' : ''">
            <div class="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 transition-all"
              :class="i + 1 < step ? 'bg-blue-600 text-white' : i + 1 === step ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900/30' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'">
              <svg v-if="i + 1 < step" class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
              </svg>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <div v-if="i < steps.length - 1" class="flex-1 h-0.5 mx-1 rounded-full"
              :class="i + 1 < step ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"></div>
          </div>
        </template>
      </div>
      <div class="flex mt-2">
        <template v-for="(s, i) in steps" :key="i">
          <span class="text-[9px] font-medium text-center leading-tight"
            :class="[i < steps.length - 1 ? 'flex-1' : 'flex-none', i + 1 <= step ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-600']">
            {{ s }}
          </span>
        </template>
      </div>
    </div>
  `,
  data: () => ({ steps: TIMELINE_STEPS }),
}

const DesktopTimeline = {
  props: ['step'],
  template: `
    <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-6 py-4">
      <div class="flex items-center">
        <template v-for="(s, i) in steps" :key="i">
          <div class="flex items-center" :class="i < steps.length - 1 ? 'flex-1' : ''">
            <div class="flex items-center gap-2 flex-shrink-0">
              <div class="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs transition-all"
                :class="i + 1 < step ? 'bg-blue-600 text-white' : i + 1 === step ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900/30' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'">
                <svg v-if="i + 1 < step" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                </svg>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="text-xs font-medium whitespace-nowrap"
                :class="i + 1 <= step ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'">
                {{ s }}
              </span>
            </div>
            <div v-if="i < steps.length - 1" class="flex-1 h-0.5 mx-3 rounded-full"
              :class="i + 1 < step ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"></div>
          </div>
        </template>
      </div>
    </div>
  `,
  data: () => ({ steps: TIMELINE_STEPS }),
}

// ── State ────────────────────────────────────────────────────────────────────

const flash              = ref(null)
const loading            = ref(true)
const loadError          = ref(null)
const submitting         = ref(false)
const verification       = ref(null)
const showForm           = ref(false)
const privacyAccepted    = ref(false)
const showWithdrawModal  = ref(false)
const withdrawing        = ref(false)
const showRevokeModal    = ref(false)
const revoking           = ref(false)

let docIdCounter = 0
const documentEntries = ref([newDoc()])

function newDoc() {
  return { id: ++docIdCounter, type: '', frontFile: null, backFile: null, frontPreview: null, backPreview: null }
}

const settingsStore = useSettingsStore()
const userEmail = computed(() => settingsStore.current_user?.email ?? '')
const userUrl   = computed(() => settingsStore.current_user?.url ?? '')

const documentTypes = [
  { value: 'id_card',         label: 'ID Card',         icon: '🪪' },
  { value: 'resident_permit', label: 'Resident Permit',  icon: '📄' },
  { value: 'passport',        label: 'Passport',         icon: '📕' },
  { value: 'driving_license', label: 'Driving License',  icon: '🚗' },
]

const benefits = [
  'Verified badge on your public profile',
  'Higher trust and credibility with visitors',
  'Increased booking confidence',
]

const howItWorks = [
  'Upload at least 1 identity document with front and back photos.',
  'Our team reviews your submission within 2 to 3 business days.',
  'We schedule a short video call to confirm your identity.',
  'Once confirmed, your verified badge goes live.',
]

// ── Computed ─────────────────────────────────────────────────────────────────

const completeCount = computed(() =>
  documentEntries.value.filter(d => d.type && d.frontFile && d.backFile).length
)

const canSubmit = computed(() => completeCount.value >= 1 && privacyAccepted.value)

function availableTypesFor(idx) {
  const taken = new Set(
    documentEntries.value
      .filter((d, i) => i !== idx && d.type)
      .map(d => d.type)
  )
  return documentTypes.filter(dt => !taken.has(dt.value))
}

const hasAvailableTypes = computed(() => {
  const taken = new Set(documentEntries.value.filter(d => d.type).map(d => d.type))
  return documentTypes.some(dt => !taken.has(dt.value))
})

// ── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
  await settingsStore.fetchIfStale()
  await loadVerification()
})

// ── Methods ──────────────────────────────────────────────────────────────────

async function loadVerification() {
  loading.value = true
  loadError.value = null
  try {
    const headers = { 'X-CSRF-TOKEN': document.querySelector('[name="csrf-token"]').getAttribute('content') }
    const res = await axios.get('/api/v1/host/settings/verification', { headers })
    if (res.data.success) {
      verification.value = res.data.data
    } else {
      loadError.value = res.data.error || 'Failed to load verification status.'
    }
  } catch {
    loadError.value = 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}

function addDocument() {
  documentEntries.value.push(newDoc())
}

function removeDocument(idx) {
  documentEntries.value.splice(idx, 1)
}

function handleFileChange(event, idx, side) {
  const file = event.target.files[0]
  if (!file) return
  const doc = documentEntries.value[idx]
  doc[side + 'File'] = file
  doc[side + 'Preview'] = URL.createObjectURL(file)
}

function cancelForm() {
  showForm.value = false
  documentEntries.value = [newDoc()]
  privacyAccepted.value = false
}

async function submitVerification() {
  if (!canSubmit.value || submitting.value) return
  submitting.value = true

  const formData = new FormData()
  let docIndex = 0
  for (const doc of documentEntries.value) {
    if (!doc.type || !doc.frontFile || !doc.backFile) continue
    formData.append(`documents[${docIndex}][document_type]`, doc.type)
    formData.append(`documents[${docIndex}][front]`, doc.frontFile)
    formData.append(`documents[${docIndex}][back]`, doc.backFile)
    docIndex++
  }

  try {
    const headers = { 'X-CSRF-TOKEN': document.querySelector('[name="csrf-token"]').getAttribute('content') }
    const res = await axios.post('/api/v1/host/settings/verification', formData, { headers })
    if (res.data.success) {
      verification.value = res.data.data
      showForm.value = false
      flash.value?.show('Your documents have been submitted. We will be in touch soon.', 'notice')
    } else {
      flash.value?.show(res.data.error || 'Submission failed. Please try again.', 'alert')
    }
  } catch {
    flash.value?.show('Something went wrong. Please try again.', 'alert')
  } finally {
    submitting.value = false
  }
}

async function confirmWithdraw() {
  withdrawing.value = true
  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('[name="csrf-token"]').getAttribute('content'),
    }
    const res = await axios.delete('/api/v1/host/settings/verification', { headers })
    if (res.data.success) {
      verification.value = null
      showWithdrawModal.value = false
      flash.value?.show('Verification request withdrawn.', 'notice')
    } else {
      flash.value?.show(res.data.error || 'Could not withdraw.', 'alert')
      showWithdrawModal.value = false
    }
  } catch {
    flash.value?.show('Something went wrong. Please try again.', 'alert')
    showWithdrawModal.value = false
  } finally {
    withdrawing.value = false
  }
}

async function revokeVerification() {
  revoking.value = true
  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('[name="csrf-token"]').getAttribute('content'),
    }
    const res = await axios.delete('/api/v1/host/settings/verification/revoke', { headers })
    if (res.data.success) {
      verification.value = null
      showRevokeModal.value = false
      flash.value?.show('Your verification has been removed. You can reapply at any time.', 'notice')
    } else {
      flash.value?.show(res.data.error || 'Could not remove verification.', 'alert')
      showRevokeModal.value = false
    }
  } catch {
    flash.value?.show('Something went wrong. Please try again.', 'alert')
    showRevokeModal.value = false
  } finally {
    revoking.value = false
  }
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

function formatDateTime(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .relative, .modal-leave-active .relative { transition: transform 0.2s ease, opacity 0.2s ease; }
.modal-enter-from .relative { transform: scale(0.95); opacity: 0; }
.modal-leave-to .relative { transform: scale(0.95); opacity: 0; }
</style>
